// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import config from "@config";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/employee/actionCreator";
import {
  GetEmployeeRoleSummary,
  GetEmployeePositionSchema,
  AddEmployeePosition,
  UpdateEmployeePosition,
  GetEmployeePositionChangelog,
  GetEmployeeSpecialContacts,
  DeleteEmployeeSpecialContact,
} from "@modules/employee/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import type { RolesSummary } from "@modules/employee/reducer/employment-details/types";
import { parseSchema } from "@utils/schema";

type EmployeeRoleRequest = $Exact<{
  clientId: string,
  employeeId: string,
  page?: string,
}>;

type EmployeeRoleRequestAddUpdate = $Exact<{
  clientId: string,
  employeeId: string,
  formData: Object,
  positionId?: number | string,
}>;

export function* onGetEmployeeRoleSummary({
  payload,
}: Action<EmployeeRoleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const page = payload.page || 1;
    const params = {
      page,
      limit: config.resultsPerPage,
    };

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      {
        params,
      },
    );

    yield put(actionCreators.GetEmployeeRoleSummarySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeRoleSummaryError(e.response));
  }
}

type EmployeePositionSchemaRequest = $Exact<{
  clientId: number,
}>;

export function* onGetEmployeePositionSchema({
  payload,
}: Action<EmployeePositionSchemaRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const schema = yield parseSchema("EmployeePositionDTO", { clientId: payload.clientId });
    yield put(actionCreators.GetEmployeePositionSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetEmployeePositionSchemaError(e.response));
  }
}

export function* onAddEmployeePosition({
  payload,
}: Action<EmployeeRoleRequestAddUpdate>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      payload.formData,
    );

    yield all([
      put(actionCreators.ToggleAddEmployeePositionModal(false)),
      put(actionCreators.GetEmployeeRoleSummary(payload)),
      put(
        actionCreators.GetEmployeePositionChangelog({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          page: 1,
        }),
      ),
      put(sendSuccessMessage("EMPLOYEE_ROLE_ADDED")),
      put(
        actionCreators.GetEmployees({
          clientId: payload.clientId,
          filters: {
            id: payload.employeeId,
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAddAllergyError(e.response));
  }
}

export function* onUpdateEmployeePosition({
  payload,
}: Action<EmployeeRoleRequestAddUpdate>): Generator<*, void, void> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.employeeId ||
      !payload.formData ||
      !payload.positionId
    ) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position/${payload.positionId}`,
      payload.formData,
    );

    yield all([
      put(actionCreators.toggleEditEmployeePositionModal(false)),
      put(actionCreators.UpdateEmployeePositionSuccess()),
      put(
        actionCreators.GetEmployeePositionChangelog({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          page: 1,
        }),
      ),
      put(
        actionCreators.GetEmployeeRoleSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage("EMPLOYEE_ROLE_UPDATED")),
      put(
        actionCreators.GetEmployees({
          clientId: payload.clientId,
          filters: {
            id: payload.employeeId,
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeePositionError(e.response));
  }
}

export function* onGetEmployeePositionChangelog({
  payload,
}: Action<EmployeeRoleRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const page = payload.page || 1;
    const params = {
      page,
      limit: config.resultsPerPage,
    };

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position/changelog`,
      {
        params,
      },
    );

    yield put(actionCreators.GetEmployeePositionChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeePositionChangelogError(e.response));
  }
}

export function* onGetEmployeeSpecialContacts({
  payload,
}: Action<EmployeeRoleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/special-contact`,
    );

    yield put(actionCreators.GetEmployeeSpecialContactsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeSpecialContactsError(e.response));
  }
}

type DeleteEmployeeSpecialContactRequest = $Exact<{
  ...EmployeeRoleRequest,
  specialContactId: number,
}>;

export function* onDeleteEmployeeSpecialContact({
  payload,
}: Action<DeleteEmployeeSpecialContactRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.specialContactId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/special-contact/${payload.specialContactId}`,
    );

    yield all([
      put(actionCreators.DeleteEmployeeSpecialContactSuccess(response.data)),
      put(
        actionCreators.GetEmployeeSpecialContacts({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteEmployeeSpecialContactError(e.response));
  }
}

export function* EmploymentDetailsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetEmployeeRoleSummary, onGetEmployeeRoleSummary),
    takeEvery(GetEmployeePositionSchema, onGetEmployeePositionSchema),
    takeEvery(AddEmployeePosition, onAddEmployeePosition),
    takeEvery(UpdateEmployeePosition, onUpdateEmployeePosition),
    takeEvery(GetEmployeePositionChangelog, onGetEmployeePositionChangelog),
    takeEvery(GetEmployeeSpecialContacts, onGetEmployeeSpecialContacts),
    takeEvery(DeleteEmployeeSpecialContact, onDeleteEmployeeSpecialContact),
  ]);
}

export default EmploymentDetailsSagas;
